import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShoppingBag, ArrowLeft, EyeOff } from "lucide-react";
import { useT } from "../i18n";
import { getProducts, getCart, cartCount } from "../lib/shopApi";
import { ProductCard } from "../components/shop/ProductCard";
import { CartDrawer } from "../components/shop/CartDrawer";
import { CheckoutForm } from "../components/shop/CheckoutForm";
import { AddProductForm } from "../components/shop/AddProductForm";
import { LangToggle } from "../components/Nav";
import { toast } from "../components/ui/sonner";

export default function Shop() {
  const t = useT();
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [count, setCount] = useState(cartCount());
  const checkoutRef = useRef(null);

  const startCheckout = () => {
    setCartOpen(false);
    setCheckingOut(true);
    setTimeout(() => checkoutRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
  };

  const load = () => getProducts().then(setProducts);

  useEffect(() => {
    load();
    if (params.get("cancelled")) toast.error(t.sh_cancelled);
    const onUpdate = () => setCount(cartCount());
    window.addEventListener("cart-updated", onUpdate);
    return () => window.removeEventListener("cart-updated", onUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-bergamote-50">
      <header className="sticky top-0 z-40 bg-cream-50/85 backdrop-blur-md border-b border-bergamote-600/15" data-testid="shop-header">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between gap-4">
          <Link to="/" data-testid="shop-back-home" className="flex items-center gap-2 text-bergamote-800 hover:text-saffron-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <img src="/nav-logo.png" alt="la bergamote" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <LangToggle />
            <button
              data-testid="shop-cart-button"
              onClick={() => setCartOpen(true)}
              className="relative inline-flex items-center gap-2 rounded-full bg-bergamote-600 text-cream-50 px-5 py-2.5 text-sm font-semibold hover:bg-bergamote-700 transition-colors"
            >
              <ShoppingBag className="w-4 h-4" />
              {t.sh_cart}
              {count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-saffron-500 text-bergamote-900 text-[11px] font-bold flex items-center justify-center" data-testid="cart-count">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="flex items-center gap-2 justify-center mb-8 text-bergamote-600" data-testid="shop-private-note">
          <EyeOff className="w-4 h-4" />
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-center">{t.sh_private}</p>
        </div>

        <h1 className="font-serif font-semibold tracking-tight text-4xl sm:text-5xl text-bergamote-900 text-center" data-testid="shop-page-title">
          {t.sh_title}
        </h1>

        <h2 className="mt-12 font-serif font-semibold tracking-tight text-2xl sm:text-3xl text-bergamote-900" data-testid="shop-specialties-heading">
          {t.sh_specialties}
        </h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shop-products-grid">
          {products.filter((p) => p.category !== "tea").map((p) => (
            <ProductCard key={p.id} product={p} onChanged={load} />
          ))}
        </div>

        <div className="my-14 border-t-2 border-bergamote-600/30" data-testid="shop-tea-divider" />

        <h2 className="font-serif font-semibold tracking-tight text-2xl sm:text-3xl text-bergamote-900" data-testid="shop-tea-heading">
          {t.sh_tea}
        </h2>
        {products.some((p) => p.category === "tea") ? (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shop-tea-grid">
            {products.filter((p) => p.category === "tea").map((p) => (
              <ProductCard key={p.id} product={p} onChanged={load} />
            ))}
          </div>
        ) : (
          <p className="mt-6 text-bergamote-500 italic" data-testid="shop-tea-empty">{t.sh_tea_empty}</p>
        )}

        {checkingOut && getCart().length > 0 && (
          <section ref={checkoutRef} className="mt-14 max-w-xl mx-auto bg-cream-50 border border-bergamote-600/15 rounded-3xl p-6 sm:p-10 scroll-mt-24" data-testid="checkout-section">
            <h2 className="font-serif text-2xl font-semibold text-bergamote-900 mb-6">{t.sh_checkout}</h2>
            <CheckoutForm />
          </section>
        )}

        <div className="mt-16 max-w-xl mx-auto">
          <AddProductForm onAdded={load} />
        </div>
      </main>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} onCheckout={startCheckout} />
    </div>
  );
}
